/* eslint-disable import/no-restricted-paths */
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { LoadingIndicator } from "@sunrun/experience-ui-components";
import { Contact } from "@sunrun/experience-ui-components/lib/components/ContactCard";
import {
  HeaderButton,
  HeaderContentLayout,
} from "../templates/HeaderContentLayout";
import { useAppSelector } from "store";
import { ROLES } from "utils/usageUtils";
import { useContactsData } from "utils/swrHooks";
import { TitleInfoContent } from "components/organisms/TitleInfoContent";
import { useOfferList } from "checkout/hooks/useCheckoutSwr";
import { findContact, getPrimaryContact } from "utils/contacts";
import { useProposals } from "utils/swrHooks";
import { Slideout } from "components/molecules/ConfirmInfo/Slideout";

const TitleInfoSlideout = ({
  onClose,
  titleNames,
}: {
  onClose: () => void;
  titleNames: string[];
}) => {
  const { prospectId } = useParams();
  const authKey = useAppSelector((state) => state?.auth?.hybridToken);
  const { refetch, contacts } = useContactsData(prospectId!, authKey);
  const cognitoToken = useAppSelector(
    (state) => state.auth.cognitoToken?.token
  );

  const { data: proposals } = useProposals(prospectId!, authKey);

  useEffect(() => {
    if (contacts?.length) {
      const primaryContact = getPrimaryContact(contacts);
      if (primaryContact !== selectedPrimaryContact) {
        setSelectedPrimaryContact(primaryContact);
      }
      if (!selectedSecondaryContact) {
        setSelectedSecondaryContact(
          findContact(contacts, ROLES.CONTRACTCOSIGNER)
        );
      }
      if (!selectedUtilityContact) {
        setSelectedUtilityContact(
          findContact(contacts, ROLES.UTILITYBILLCONTACT)
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts]);

  const { offerList, isOfferListLoading } = useOfferList(
    prospectId!,
    cognitoToken
  );

  const hasOffers = !!proposals?.length || !!offerList?.length;

  const hasSignedOffer = useMemo(() => {
    return !!proposals?.find(({ signed }) => signed);
  }, [proposals]);

  const offerApproved = useMemo(() => {
    return !!proposals?.find((offer) => offer?.stage === "SR Approved");
  }, [proposals]);

  const [selectedUtilityContact, setSelectedUtilityContact] =
    useState<Contact>();
  const [selectedPrimaryContact, setSelectedPrimaryContact] =
    useState<Contact>();

  const [selectedSecondaryContact, setSelectedSecondaryContact] =
    useState<Contact>();

  const handleCancel = onClose;

  const buttons = useMemo(() => {
    return [
      {
        type: "secondary",
        text: "Close",
        onClick: handleCancel,
      } as HeaderButton,
    ];
  }, [handleCancel]);

  if (isOfferListLoading) {
    return (
      <Slideout>
        <LoadingIndicator fullScreen color="black" />;
      </Slideout>
    );
  }

  return (
    <Slideout onClose={onClose}>
      <HeaderContentLayout
        title="Customer & Title Info"
        description="You can edit customer's name and contact information."
        buttons={buttons}
        content={
          <TitleInfoContent
            selectedPrimaryContact={selectedPrimaryContact}
            setSelectedPrimaryContact={(contact) => {
              setSelectedPrimaryContact(contact);
            }}
            selectedSecondaryContact={selectedSecondaryContact}
            setSelectedSecondaryContact={(contact) => {
              setSelectedSecondaryContact(contact);
            }}
            selectedUtilityContact={selectedUtilityContact}
            setSelectedUtilityContact={(contact) => {
              setSelectedUtilityContact(contact);
            }}
            hasOffers={hasOffers}
            hasSignedOffer={hasSignedOffer}
            offerApproved={offerApproved}
            refetchContacts={refetch}
            namesOnTitle={titleNames}
          />
        }
      />
    </Slideout>
  );
};

export { TitleInfoSlideout };
